import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import type { Theme } from './slideTypes';
import { RICH_TEXT_STYLES } from './shared/SlideShared';

// Rich text editor with floating bubble menu (Medium/Ghost-style)
// variant='body' shows full formatting; variant='heading' shows only bold/italic
export function SlideRichTextEditor({
  value,
  onChange,
  placeholder,
  theme,
  textStyle,
  variant = 'body',
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  theme: Theme;
  textStyle?: React.CSSProperties;
  variant?: 'body' | 'heading';
}) {
  const [linkInputActive, setLinkInputActive] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  const extensions = useMemo(() => [
    StarterKit.configure({ link: false, underline: false }),
    Placeholder.configure({ placeholder: placeholder || 'Enter content...' }),
    Link.configure({ openOnClick: false, HTMLAttributes: { class: 'underline text-blue-400' } }),
    Underline,
  ], [placeholder]);

  const editorProps = useMemo(() => ({
    attributes: { class: 'slide-rich-editor focus:outline-none' },
  }), []);

  const lastEmittedHtml = useRef(value || '');

  const editor = useEditor({
    extensions,
    content: value || '',
    editorProps,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastEmittedHtml.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (editor && value !== lastEmittedHtml.current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
      lastEmittedHtml.current = value || '';
    }
  }, [value, editor]);

  useEffect(() => {
    if (linkInputActive && linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [linkInputActive]);

  const openLinkInput = useCallback(() => {
    if (!editor) return;
    const existingHref = editor.getAttributes('link').href || '';
    setLinkUrl(existingHref);
    setLinkInputActive(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl.trim()) {
      editor.chain().focus().setLink({ href: linkUrl.trim() }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setLinkInputActive(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const cancelLink = useCallback(() => {
    setLinkInputActive(false);
    setLinkUrl('');
    editor?.chain().focus().run();
  }, [editor]);

  if (!editor) return null;

  const btnClass = (active: boolean) =>
    `p-1.5 rounded-md transition-all duration-100 ${
      active
        ? 'text-white bg-white/20'
        : 'text-white/70 hover:text-white hover:bg-white/10'
    }`;

  const iconSize = 15;
  const strokeWidth = 2;

  return (
    <div className="w-full">
      <BubbleMenu
        editor={editor}
        options={{
          placement: 'top',
          offset: { mainAxis: 8 },
        }}
      >
        <div
          className="flex items-center gap-0.5 px-1.5 py-1 rounded-xl shadow-xl"
          style={{
            background: 'rgba(17, 17, 17, 0.95)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            animation: 'bubbleIn 150ms ease-out',
          }}
        >
          {linkInputActive ? (
            <div className="flex items-center gap-1 px-1">
              <input
                ref={linkInputRef}
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                  if (e.key === 'Escape') { e.preventDefault(); cancelLink(); }
                }}
                placeholder="Paste URL…"
                className="bg-transparent text-white text-xs outline-none placeholder-white/40 w-44 py-0.5"
              />
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); applyLink(); }}
                className="text-white/70 hover:text-white p-1 rounded"
                title="Apply"
              >
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); cancelLink(); }}
                className="text-white/70 hover:text-white p-1 rounded"
                title="Cancel (Esc)"
              >
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          ) : (
            <>
              {/* Bold */}
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
                className={btnClass(editor.isActive('bold'))}
                title="Bold (Cmd+B)"
              >
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>
              </button>
              {/* Italic */}
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
                className={btnClass(editor.isActive('italic'))}
                title="Italic (Cmd+I)"
              >
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>
              </button>
              {variant === 'body' && (
                <>
                  {/* Underline */}
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
                    className={btnClass(editor.isActive('underline'))}
                    title="Underline (Cmd+U)"
                  >
                    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" y1="20" x2="20" y2="20"/></svg>
                  </button>
                  {/* Strikethrough */}
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleStrike().run(); }}
                    className={btnClass(editor.isActive('strike'))}
                    title="Strikethrough"
                  >
                    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M16 4H9a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h1"/><path d="M12 15h3a3 3 0 0 1 0 6H8"/><line x1="4" y1="12" x2="20" y2="12"/></svg>
                  </button>
                  {/* Divider */}
                  <div className="w-px h-4 mx-0.5 bg-white/20" />
                  {/* Link */}
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); openLinkInput(); }}
                    className={btnClass(editor.isActive('link'))}
                    title="Link"
                  >
                    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  </button>
                  {/* Divider */}
                  <div className="w-px h-4 mx-0.5 bg-white/20" />
                  {/* Bullet list */}
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
                    className={btnClass(editor.isActive('bulletList'))}
                    title="Bullet list"
                  >
                    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1" fill="currentColor"/><circle cx="3" cy="12" r="1" fill="currentColor"/><circle cx="3" cy="18" r="1" fill="currentColor"/></svg>
                  </button>
                  {/* Numbered list */}
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
                    className={btnClass(editor.isActive('orderedList'))}
                    title="Numbered list"
                  >
                    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><text x="1" y="8" fill="currentColor" fontSize="8" fontFamily="system-ui" fontWeight="600">1</text><text x="1" y="14" fill="currentColor" fontSize="8" fontFamily="system-ui" fontWeight="600">2</text><text x="1" y="20" fill="currentColor" fontSize="8" fontFamily="system-ui" fontWeight="600">3</text></svg>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </BubbleMenu>
      <EditorContent
        editor={editor}
        style={{ minHeight: variant === 'heading' ? '2em' : '6em', ...textStyle }}
      />
      <style>{`
        @keyframes bubbleIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
